/**
 * @fileoverview A cell renderer component for AG Grid that displays an icon button.
 * Used to render interactive icon buttons within grid cells.
 */

import * as React from 'react';
import { FunctionComponent } from 'react';

import { IconButton, Flex } from 'basecamp';
import { ICellRendererParams } from '@ag-grid-community/all-modules';

/**
 * Props interface for the IconButtonRenderer component
 * @interface Props
 * @property {function} onClick - Callback function when button is clicked, takes an ID parameter
 * @property {ICellRendererParams} params - AG Grid cell renderer parameters
 * @property {string} icon - Name of the icon to display in the button
 */
type Props = {
	onClick: (id: number) => void;
	icon: string;
	dataTest?: string;
};

/**
 * IconButtonRenderer Component
 *
 * Renders an icon button within an AG Grid cell with centred alignment.
 * The button triggers the provided onClick handler with the row's ID when clicked.
 *
 * @component
 * @param {Props} props - Component props
 * @param {function} props.onClick - Click handler function
 * @param {string} props.icon - Icon name to display
 * @param {Object} props.params - AG Grid cell parameters containing row data
 * @returns {JSX.Element} Rendered icon button component
 */
const IconButtonRenderer: FunctionComponent<Props> = ({ onClick, icon, dataTest }) => {
	return (
		<Flex setAs="row" withAlign="centerLeft" height="100%">
			<IconButton
				withIcon={icon}
				setAs="primary"
				withSize="small"
				onClick={onClick}
				dataTest={dataTest}
			/>
		</Flex>
	);
};

export default IconButtonRenderer;
